import ga4 from '#common/utils/ga4'
import { identifyUser } from '#common/utils/user'
import { SESSION_COOKIE_NAME } from '#nuxt/modules/djangoAuth/auth.utils'

const EMPTY_USER = {
  first_name: '',
  last_name: '',
  is_authenticated: false,
  analytics_id: null,
  partner_id: 23,
  state: undefined,
  zipcode: undefined,
  status: undefined,
  memberships: [],
  braintree_token: undefined,
  notifyme_products: {},
  notifyme_wineries: {},
  push_notifications: [],
  sessionid: undefined,
}

export const useAuth = defineStore('auth', () => {
  const { $store, $cookies, $config } = useNuxtApp()
  const requestFetch = useRequestFetch()
  const metadata = useMetadata()
  const paymentMethods = usePaymentMethods()
  const router = useRouter()
  const sessionidCookie = useCookie(SESSION_COOKIE_NAME, {
    domain: $config.public.cookieDomain,
    sameSite: 'lax',
    secure: true,
    maxAge: 60 * 60 * 24 * 30, // 30 days
  })

  // from login.js
  const authModalOpen = ref(false)
  const authModalTheme = ref({})
  const authStep = ref('email-check')
  const authEmail = ref(null)
  const authZipcode = ref(null)
  const authRedirect = ref(null)
  const authIsClub = ref(false)
  const qes = ref(false)
  const checkUserExistsError = ref(null)

  const userLoaded = ref(false)
  const user = ref({ ...EMPTY_USER })
  const userRegisteredFromLoginModal = ref(false)
  const userLoggedInFromLoginModal = ref(false)

  const braintreeStore = useBraintree()

  const loggedIn = computed(() => user?.value?.is_authenticated)
  const possessiveTitleUserName = computed(() => {
    const firstName = user?.value?.first_name
    if (!firstName) return "User's"

    return `${firstName.charAt(0).toUpperCase()}${firstName.slice(1)}${
      firstName.endsWith('s') ? "'" : "'s"
    }`
  })

  const openAuthModal = ({
    theme,
    step,
    email,
    zipcode,
    redirect,
    isClub,
  } = {}) => {
    authModalOpen.value = true
    if (theme !== undefined) authModalTheme.value = theme
    if (step !== undefined) authStep.value = step
    if (email !== undefined) authEmail.value = email
    if (zipcode !== undefined) authZipcode.value = zipcode
    if (isClub !== undefined) authIsClub.value = isClub
    authRedirect.value =
      redirect !== undefined ? redirect : router.currentRoute.value.fullPath
  }

  const closeAuthModal = () => {
    authModalOpen.value = false
    checkUserExistsError.value = false
  }

  const resetAuthModal = () => {
    closeAuthModal()
    authModalTheme.value = {}
    authStep.value = 'email-check'
    authEmail.value = null
    authZipcode.value = null
    authRedirect.value = null
    authIsClub.value = false
    checkUserExistsError.value = false
  }

  const setAuthStep = (step) => {
    authStep.value = step
  }

  const requireLogin = async ({ redirect, isClub = false } = {}) => {
    if (loggedIn.value) return true

    openAuthModal({ redirect, isClub })

    // Wait until user logs in or closes modal
    return await new Promise((resolve) => {
      const stop = watch(
        () => loggedIn.value || !authModalOpen.value,
        (val) => {
          if (val) {
            stop()
            if (!loggedIn.value) {
              // User closed the modal without logging in
              navigateTo('/')
            }
            resolve(loggedIn.value)
          }
        },
      )
    })
  }

  const setUser = (newUser) => {
    user.value = newUser
    userLoaded.value = true
    if (user.value?.is_authenticated) {
      identifyUser({ user: user.value }, $cookies)
    }
  }

  const checkUserExists = async (email) => {
    checkUserExistsError.value = null
    try {
      const response = await requestFetch('/api/customer/check_email/', {
        method: 'POST',
        body: JSON.stringify({ email }),
      })

      if (response?.non_field_errors?.length) {
        checkUserExistsError.value = response.non_field_errors
        return
      }

      authEmail.value = email
      authZipcode.value = response.zip_code || null
      setAuthStep(response.user ? 'login' : 'register')
    } catch (err) {
      if (err.data?.non_field_errors?.length) {
        checkUserExistsError.value = err.data.non_field_errors
        return
      }
      checkUserExistsError.value = [
        'Error: Something went wrong. Please try again.',
      ]
      console.error('checkUserExists failed:', err)
    }
  }

  const loadUser = async (force = false) => {
    if (!force && userLoaded.value) return
    userLoaded.value = true
    const data = await requestFetch(`/server/session`)
    setUser(data)
  }

  const registerUser = async (data) => {
    let response = { data: {} }
    try {
      const regData = {
        action: 'register',
        sessionID: sessionidCookie.value,

        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        default_zipcode: data.default_zipcode,
        date_of_birth: data.date_of_birth,
        password: data.password,
        is_club: data.loginIsClub || false,
        recaptcha: data.recaptcha,

        receive_shipping_updates_by_sms: data.receive_shipping_updates_by_sms,
        email_subscription_status: data.email_subscription_status,
        cold_pack_option: data.cold_pack_option,
        address: data.address,
      }
      response = await requestFetch('/server/authenticate', {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(regData),
      })
      if (response?.statusCode === 400) {
        throw new Error(response?.data?.non_field_errors?.[0])
      }
      sessionidCookie.value = response.data.sessionid
      setUser(response.data.user)
      braintreeStore.resetBraintree()
      if (authRedirect.value) navigateTo(authRedirect.value)
      resetAuthModal()
    } catch (e) {
      if (e.message && e.message.includes('status code 429')) {
        throw new exceptions.RateLimitedException(
          'Too many login attempts. Please try again later or reset your password by clicking "Forgot Your Password?" below.',
        )
      }
      throw e
    }
    ga4.track('register')
    userRegisteredFromLoginModal.value = !!data.registeredFromLoginModal
    nextTick(async () => {
      await metadata.loadAll(true)
      $store.dispatch('getCart')
      $store.dispatch('getPromoBanner')
    })
    return response.data
  }

  const loginUser = async (params) => {
    try {
      const response = await requestFetch('/server/authenticate', {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify({
          action: 'login',
          sessionID: sessionidCookie.value,

          email: params.email,
          password: params.password,
          path: params?.path ?? route.path,
          recaptcha: params?.recaptcha,
        }),
      })
      sessionidCookie.value = response.sessionid
      setUser(response.user)
      braintreeStore.resetBraintree()
      navigateTo(authRedirect.value)
      resetAuthModal()
    } catch (e) {
      if (e.message && e.message.includes('status code 429')) {
        throw new exceptions.RateLimitedException(
          'Too many login attempts. Please try again later or reset your password by clicking "Forgot Your Password?" below.',
        )
      }
      throw e
    }
    ga4.track('login')
    userLoggedInFromLoginModal.value = !!params.loggedInFromLoginModal

    nextTick(async () => {
      await metadata.loadAll(true)
      $store.dispatch('getCart')
      $store.dispatch('getPromoBanner')
      $store.dispatch('fetchPPTOEligibleOrder')
    })
  }

  const changePasswordForUser = async (body) => {
    try {
      const response = await requestFetch('/server/change_password', {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(body),
      })
      sessionidCookie.value = response.sessionid
      setUser(response.user)
      resetAuthModal()
    } catch (e) {
      console.log('changePasswordForUser error', JSON.stringify(e))
      if (e.message && e.message.includes('status code 429')) {
        throw new exceptions.RateLimitedException(
          'Too many login attempts. Please try again later or reset your password by clicking "Forgot Your Password?" below.',
        )
      }
      throw e
    }
    ga4.track('change_password')

    await metadata.loadAll(true)
    $store.dispatch('getCart')
    $store.dispatch('getPromoBanner')
    $store.dispatch('fetchPPTOEligibleOrder')
  }

  const logoutUser = async () => {
    user.value = { ...EMPTY_USER }
    userLoggedInFromLoginModal.value = false
    userRegisteredFromLoginModal.value = false
    sessionidCookie.value = null // setting to null deletes the cookie!
    const response = await requestFetch('/server/logout')
    setUser(response.user)
    braintreeStore.resetBraintree()
    resetAuthModal()

    ga4.track('logout')

    await metadata.loadAll(true)
    paymentMethods.clearPaymentMethods()
    $store.dispatch('setNotifications', [])
    $store.dispatch('getCart')
    $store.dispatch('getPromoBanner', [])

    navigateTo('/')
  }

  const registerQes = async (data) => {
    await requestFetch(`/api/customer/qes/`, {
      method: 'POST',
      body: JSON.stringify({
        email: data.qesEmail,
        zip: data.zipcode,
        daily_email_active: true,
      }),
    })
    qes.value = true
  }

  return {
    authModalOpen,
    authModalTheme,
    authStep,
    authEmail,
    authZipcode,
    authRedirect,
    authIsClub,
    openAuthModal,
    closeAuthModal,
    setAuthStep,
    checkUserExists,
    checkUserExistsError,
    qes,
    registerQes,

    userLoaded,
    user,
    loggedIn,
    userRegisteredFromLoginModal,
    userLoggedInFromLoginModal,
    possessiveTitleUserName,
    setUser,
    loadUser,
    registerUser,
    changePasswordForUser,
    loginUser,
    logoutUser,
    requireLogin,
    resetAuthModal,
  }
})
